import {
  DESKTOP_PATH,
  DRIVE_PATH,
  MY_COMPUTER_PATH,
  MY_DOCUMENTS_PATH,
  MY_PICTURES_PATH,
} from '@/config/filesystem';
import { playlistM3u } from '@/config/media';
import { readContent } from './content';
import { tracksIn } from './media';
import { iconFor } from './icons';
import { openFile } from './openFile';
import { basename, dirname, join, list, normalize, parent, resolve } from './resolve';
import type { FsApi, FsFile } from './types';

async function readText(node: FsFile): Promise<string> {
  if (node.data.kind === 'text' || node.data.kind === 'markdown') {
    return readContent(node.data.src);
  }
  if (node.data.kind === 'url') {
    // Real .url files are INI-shaped, and Notepad happily shows that.
    return `[InternetShortcut]\r\nURL=${node.data.href}\r\n`;
  }
  // Same idea one step further: an .m3u is a list of tracks in plain text,
  // and the tracks it names are the ones in the folder it is sitting in. Read
  // at the moment of asking, so a deleted track is a line that is no longer
  // written — which is what saving the playlist again would have done.
  if (node.data.kind === 'playlist') return playlistM3u(tracksIn(dirname(node.path)));
  return '';
}

/**
 * A single frozen object handed to every app. It's stable across renders, so
 * apps can safely depend on it in effects.
 */
export const fsApi: FsApi = Object.freeze({
  resolve,
  list,
  parent,
  readText,
  openFile,
  iconFor,
  normalize,
  join,
  basename,
  dirname,
  roots: Object.freeze({
    myComputer: MY_COMPUTER_PATH,
    drive: DRIVE_PATH,
    desktop: DESKTOP_PATH,
    myDocuments: MY_DOCUMENTS_PATH,
    myPictures: MY_PICTURES_PATH,
  }),
});

export function useFs(): FsApi {
  return fsApi;
}
